import React, { useState, useMemo } from 'react';
import { Btn, Field, Input, Modal, SectionHeader } from '../components/UI';
import { COSTOS_FIJOS, fmtM } from '../utils';
import { showToast } from '../components/UI';

export default function Cuentas({ store, setCol }) {
  const { cuentasBancarias, compras, costosMes } = store;
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [lectorOpen, setLectorOpen] = useState(false);
  const [lectorImg, setLectorImg] = useState(null);
  const [lectorResult, setLectorResult] = useState(null);
  const [lectorLoading, setLectorLoading] = useState(false);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('anthropic-key') || '');

  const cuentas = cuentasBancarias || {};

  function openNew() {
    setForm({ nombre: '', alias: '', cbu: '', banco: '', tipo: 'proveedor' });
    setModal('new');
  }

  function openEdit(id) {
    setForm({ ...cuentas[id] });
    setModal(id);
  }

  function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLectorImg(ev.target.result);
      setLectorResult(null);
    };
    reader.readAsDataURL(file);
  }

  async function leerComprobante() {
    if (!lectorImg) { showToast('Seleccioná una imagen primero'); return; }
    const key = apiKey || localStorage.getItem('anthropic-key');
    if (!key) { showToast('Configurá tu API key de Anthropic'); return; }
    setLectorLoading(true);
    try {
      const base64 = lectorImg.split(',')[1];
      const mediaType = lectorImg.split(';')[0].split(':')[1];
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': key, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 500,
          messages: [{
            role: 'user',
            content: [
              { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
              { type: 'text', text: 'Este es un comprobante de transferencia bancaria. Extraé exactamente: 1) monto total transferido (solo número sin puntos ni comas, ej: 150000.50), 2) alias o CBU destino, 3) nombre o razón social del destinatario si aparece, 4) fecha del comprobante. Respondé SOLO con JSON así: {"monto": 150000.50, "alias": "alias.destino", "cbu": "2200...", "destinatario": "Nombre SA", "fecha": "2026-03-15"}. Si no encontrás algún campo ponelo como null.' }
            ]
          }]
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const text = data.content[0].text;
      const clean = text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);

      // Find matching account
      const query = (parsed.alias || parsed.cbu || '').toLowerCase();
      const matchCuenta = Object.values(cuentas).find(c =>
        (c.alias||'').toLowerCase() === query ||
        (c.cbu||'') === query ||
        (c.nombre||'').toLowerCase().includes((parsed.destinatario||'').toLowerCase().slice(0,5))
      );

      // Find matching pending payments
      let matchPagos = [];
      if (matchCuenta) {
        const nombre = matchCuenta.nombre;
        const pendCompras = Object.values(compras||{}).filter(c => !c.pagado && (c.proveedor||'').toUpperCase() === nombre.toUpperCase());
        const pendCostos = Object.values(costosMes||{}).filter(c => !c.pagado && !c.esAmortizacion && (c.nombre||'').toUpperCase() === nombre.toUpperCase());
        matchPagos = [...pendCompras.map(p=>({...p,_src:'compra'})), ...pendCostos.map(p=>({...p,_src:'costo',id:p.nombre}))];
        // Sort by closest monto match
        if (parsed.monto) {
          matchPagos.sort((a,b) => Math.abs((parseFloat(a.monto)||0)-parsed.monto) - Math.abs((parseFloat(b.monto)||0)-parsed.monto));
        }
      }

      setLectorResult({ ...parsed, matchCuenta, matchPagos });
    } catch(err) {
      console.error(err);
      showToast('Error al leer el comprobante: ' + err.message);
    }
    setLectorLoading(false);
  }

  function save() {
    if (!form.nombre) { showToast('Ingresá un nombre'); return; }
    if (!form.alias && !form.cbu) { showToast('Ingresá al menos alias o CBU'); return; }
    const id = modal === 'new' ? 'cb' + Date.now() : modal;
    const updated = { ...cuentas, [id]: { ...form, id } };
    setCol('cuentasBancarias', updated);
    showToast(modal === 'new' ? 'Cuenta agregada' : 'Cuenta actualizada');
    setModal(null);
  }

  function deleteCuenta(id) {
    const updated = { ...cuentas };
    delete updated[id];
    setCol('cuentasBancarias', updated);
    showToast('Eliminada');
  }

  // Group by tipo
  const proveedores = Object.values(cuentas).filter(c => c.tipo === 'proveedor');
  const costos = Object.values(cuentas).filter(c => c.tipo === 'costo');
  const otros = Object.values(cuentas).filter(c => c.tipo === 'otro');

  // Pending payments to cross-reference
  const pendientesCompras = Object.values(compras || {}).filter(c => !c.pagado);
  const pendientesCostos = Object.values(costosMes || {}).filter(c => !c.pagado && !c.esAmortizacion && c.monto > 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Cuentas bancarias</div>
          <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>CBU y alias de proveedores y costos fijos</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={() => setLectorOpen(true)}>📷 Leer comprobante</Btn>
          <Btn variant="grn" onClick={openNew}>+ Agregar cuenta</Btn>
        </div>
      </div>

      {/* Info panel */}
      <div style={{ background: 'var(--blu-l)', border: '1px solid #a0c4e0', borderRadius: 'var(--rl)', padding: '12px 16px', marginBottom: 16, fontSize: 12, color: 'var(--blu-m)' }}>
        <strong>¿Para qué sirve esto?</strong> Cargás el alias o CBU de cada proveedor/costo una sola vez. Cuando recibís un comprobante de pago, podés buscarlo por alias y la app te muestra automáticamente qué pagos pendientes tiene ese proveedor para que los marques como pagados.
      </div>

      {[['proveedor', 'Proveedores / Droguerías', proveedores], ['costo', 'Costos fijos', costos], ['otro', 'Otros', otros]].map(([tipo, titulo, lista]) => {
        if (!lista.length && tipo === 'otro') return null;
        return (
          <div key={tipo} style={{ marginBottom: 20 }}>
            <SectionHeader title={`${titulo} (${lista.length})`} />
            {!lista.length ? (
              <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '24px', textAlign: 'center', color: 'var(--tx3)', fontSize: 12 }}>
                Sin cuentas cargadas. Hacé clic en "+ Agregar cuenta" arriba.
              </div>
            ) : (
              <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
                {lista.map((cuenta, i) => {
                  // Find pending payments for this account
                  const pendientes = tipo === 'proveedor'
                    ? pendientesCompras.filter(c => (c.proveedor||'').toUpperCase() === (cuenta.nombre||'').toUpperCase())
                    : pendientesCostos.filter(c => (c.nombre||'').toUpperCase() === (cuenta.nombre||'').toUpperCase());
                  const totalPendiente = pendientes.reduce((a, p) => a + (parseFloat(p.monto)||0), 0);

                  return (
                    <div key={cuenta.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px', borderBottom: i < lista.length-1 ? '1px solid var(--border)' : '' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{cuenta.nombre}</div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                          {cuenta.alias && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                              <span style={{ color: 'var(--tx3)' }}>Alias:</span>
                              <code style={{ background: 'var(--surf2)', padding: '1px 7px', borderRadius: 4, fontSize: 12, fontWeight: 700, letterSpacing: '.02em' }}>{cuenta.alias}</code>
                              <button onClick={() => { navigator.clipboard?.writeText(cuenta.alias); showToast('Alias copiado'); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blu-m)', fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--blu-l)' }}>
                                Copiar
                              </button>
                            </div>
                          )}
                          {cuenta.cbu && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                              <span style={{ color: 'var(--tx3)' }}>CBU:</span>
                              <code style={{ background: 'var(--surf2)', padding: '1px 7px', borderRadius: 4, fontSize: 11, fontFamily: 'Courier, monospace' }}>{cuenta.cbu}</code>
                              <button onClick={() => { navigator.clipboard?.writeText(cuenta.cbu); showToast('CBU copiado'); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blu-m)', fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--blu-l)' }}>
                                Copiar
                              </button>
                            </div>
                          )}
                          {cuenta.banco && <span style={{ fontSize: 11, color: 'var(--tx3)' }}>{cuenta.banco}</span>}
                        </div>
                      </div>
                      {totalPendiente > 0 && (
                        <div style={{ background: 'var(--red-l)', borderRadius: 8, padding: '6px 10px', textAlign: 'center', flexShrink: 0 }}>
                          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase' }}>Pendiente</div>
                          <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 13, color: 'var(--red)' }}>{fmtM(totalPendiente)}</div>
                          <div style={{ fontSize: 9.5, color: 'var(--red)' }}>{pendientes.length} pago{pendientes.length!==1?'s':''}</div>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 5 }}>
                        <Btn size="sm" onClick={() => openEdit(cuenta.id)}>✏</Btn>
                        <Btn size="sm" variant="red" onClick={() => deleteCuenta(cuenta.id)}>×</Btn>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Quick lookup */}
      {Object.values(cuentas).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <SectionHeader title="Buscar por alias o CBU" />
          <BuscadorCuenta cuentas={cuentas} pendientesCompras={pendientesCompras} pendientesCostos={pendientesCostos} />
        </div>
      )}

      {/* Lector de comprobantes */}
      {lectorOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) { setLectorOpen(false); setLectorImg(null); setLectorResult(null); }}}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', padding: 24, width: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>📷 Leer comprobante con IA</h3>
              <button onClick={() => { setLectorOpen(false); setLectorImg(null); setLectorResult(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22 }}>×</button>
            </div>

            {/* API Key config */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>API Key de Anthropic</label>
              <input type="password" value={apiKey}
                onChange={e => { setApiKey(e.target.value); localStorage.setItem('anthropic-key', e.target.value); }}
                placeholder="sk-ant-api03-..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
            </div>

            {/* Image upload */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Comprobante</label>
              <label style={{ display: 'block', border: '2px dashed var(--border2)', borderRadius: 10, padding: 20, textAlign: 'center', cursor: 'pointer', background: lectorImg ? 'var(--grn-l)' : 'var(--surf2)' }}>
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                {lectorImg
                  ? <div><div style={{ fontSize: 24, marginBottom: 6 }}>✓</div><div style={{ fontSize: 12, color: 'var(--grn)', fontWeight: 700 }}>Imagen cargada — clic para cambiar</div></div>
                  : <div><div style={{ fontSize: 28, marginBottom: 6 }}>📎</div><div style={{ fontSize: 12, color: 'var(--tx3)' }}>Tocá para seleccionar la imagen del comprobante</div></div>
                }
              </label>
              {lectorImg && <img src={lectorImg} alt="comprobante" style={{ width: '100%', borderRadius: 8, marginTop: 10, maxHeight: 200, objectFit: 'contain' }} />}
            </div>

            <button onClick={leerComprobante} disabled={lectorLoading || !lectorImg}
              style={{ width: '100%', padding: 13, background: lectorLoading ? 'var(--tx3)' : 'var(--grn)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: lectorLoading ? 'default' : 'pointer', marginBottom: 14 }}>
              {lectorLoading ? '⏳ Analizando comprobante...' : '🔍 Analizar con IA'}
            </button>

            {/* Result */}
            {lectorResult && (
              <div style={{ background: 'var(--surf2)', borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 10 }}>Datos detectados</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                  {[
                    ['Monto', lectorResult.monto ? fmtM(lectorResult.monto) : '—'],
                    ['Fecha', lectorResult.fecha || '—'],
                    ['Alias', lectorResult.alias || '—'],
                    ['Destinatario', lectorResult.destinatario || '—'],
                    ['CBU', lectorResult.cbu || '—'],
                  ].map(([k,v]) => (
                    <div key={k}>
                      <div style={{ fontSize: 9.5, color: 'var(--tx3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: k === 'Monto' ? 'Courier, monospace' : 'inherit', color: k === 'Monto' ? 'var(--grn)' : 'var(--tx)' }}>{v}</div>
                    </div>
                  ))}
                </div>

                {lectorResult.matchCuenta && (
                  <div style={{ background: 'var(--grn-l)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--grn)', marginBottom: 4 }}>✓ Cuenta identificada: {lectorResult.matchCuenta.nombre}</div>
                  </div>
                )}

                {lectorResult.matchPagos?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx2)', marginBottom: 8 }}>Pagos pendientes que coinciden:</div>
                    {lectorResult.matchPagos.slice(0,4).map((p, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: i===0?'var(--yel-l)':'var(--surf)', borderRadius: 7, marginBottom: 6, border: `1px solid ${i===0?'#d4a843':'var(--border)'}` }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{p.proveedor || p.nombre || p.concepto}</div>
                          <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{p.fechaVenc || p.fecha || ''} · {p._src === 'compra' ? 'Compra' : 'Costo fijo'}</div>
                        </div>
                        <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 13, color: 'var(--red)' }}>{fmtM(p.monto)}</div>
                        {i === 0 && <span style={{ fontSize: 9, background: 'var(--yel-l)', color: 'var(--yel)', padding: '1px 5px', borderRadius: 99, fontWeight: 700 }}>Mejor match</span>}
                      </div>
                    ))}
                    <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 6 }}>
                      Para marcar como pagado, andá a la sección correspondiente y usá el botón "Pagar".
                    </div>
                  </div>
                )}

                {lectorResult.matchPagos?.length === 0 && lectorResult.matchCuenta && (
                  <div style={{ fontSize: 12, color: 'var(--grn)', fontWeight: 600 }}>✓ Sin pagos pendientes para esta cuenta</div>
                )}

                {!lectorResult.matchCuenta && (
                  <div style={{ fontSize: 12, color: 'var(--yel)', fontWeight: 600 }}>⚠ No se encontró la cuenta en tu lista. Agregala en "Cuentas bancarias".</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {modal && (
        <Modal title={modal === 'new' ? 'Nueva cuenta bancaria' : 'Editar cuenta'} onClose={() => setModal(null)} onSave={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Nombre / Titular" full>
              <Input value={form.nombre} onChange={v => setForm(f => ({...f, nombre: v}))} placeholder="Ej: COFARSUR, Alquiler..." autoFocus />
            </Field>
            <Field label="Tipo" full>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['proveedor','Proveedor'],['costo','Costo fijo'],['otro','Otro']].map(([val, label]) => (
                  <button key={val} onClick={() => setForm(f => ({...f, tipo: val}))}
                    style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${form.tipo === val ? 'var(--grn)' : 'var(--border2)'}`, background: form.tipo === val ? 'var(--grn-l)' : 'var(--surf2)', color: form.tipo === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Alias">
              <Input value={form.alias||''} onChange={v => setForm(f => ({...f, alias: v}))} placeholder="alias.banco.cuenta" />
            </Field>
            <Field label="CBU">
              <Input value={form.cbu||''} onChange={v => setForm(f => ({...f, cbu: v}))} placeholder="22 dígitos" />
            </Field>
            <Field label="Banco (opcional)" full>
              <Input value={form.banco||''} onChange={v => setForm(f => ({...f, banco: v}))} placeholder="Ej: Galicia, Nación..." />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BuscadorCuenta({ cuentas, pendientesCompras, pendientesCostos }) {
  const [query, setQuery] = useState('');
  const result = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.trim().toLowerCase();
    return Object.values(cuentas).find(c =>
      (c.alias||'').toLowerCase().includes(q) ||
      (c.cbu||'').includes(q) ||
      (c.nombre||'').toLowerCase().includes(q)
    );
  }, [query, cuentas]);

  const pendientes = result ? [
    ...pendientesCompras.filter(c => (c.proveedor||'').toUpperCase() === (result.nombre||'').toUpperCase()),
    ...pendientesCostos.filter(c => (c.nombre||'').toUpperCase() === (result.nombre||'').toUpperCase()),
  ] : [];

  return (
    <div>
      <input value={query} onChange={e => setQuery(e.target.value)}
        placeholder="Buscá por alias, CBU o nombre..."
        style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 14, background: 'var(--surf)', color: 'var(--tx)', outline: 'none', marginBottom: 10, boxSizing: 'border-box' }} />
      {result && (
        <div style={{ background: 'var(--surf)', border: '2px solid var(--grn)', borderRadius: 'var(--rl)', padding: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{result.nombre}</div>
          {result.alias && <div style={{ fontSize: 12, marginBottom: 4 }}><span style={{ color: 'var(--tx3)' }}>Alias: </span><code style={{ fontWeight: 700 }}>{result.alias}</code></div>}
          {result.cbu && <div style={{ fontSize: 12, marginBottom: 8 }}><span style={{ color: 'var(--tx3)' }}>CBU: </span><code>{result.cbu}</code></div>}
          {pendientes.length > 0 ? (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 8 }}>Pagos pendientes</div>
              {pendientes.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderTop: '1px solid var(--border)', fontSize: 12 }}>
                  <span>{p.concepto || p.nombre || 'Pago'}</span>
                  <span style={{ fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(p.monto)}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700 }}>
                <span>Total pendiente</span>
                <span style={{ fontFamily: 'Courier, monospace', color: 'var(--red)' }}>{fmtM(pendientes.reduce((a,p) => a+(parseFloat(p.monto)||0), 0))}</span>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--grn)', fontWeight: 600 }}>✓ Sin pagos pendientes</div>
          )}
        </div>
      )}
      {query && !result && (
        <div style={{ fontSize: 12, color: 'var(--tx3)', padding: '10px 0' }}>No se encontró ninguna cuenta con ese alias o CBU.</div>
      )}
    </div>
  );
}
